import {
  DEFAULT_SESSION_SETTINGS,
  DEFAULT_VOICE,
  MAX_AFFIRMATION_LEN,
  PRESET_LABELS,
  PRESETS,
  INTENT_EXAMPLES,
  composeAffirmations,
  suggestTitle,
  type ComposeResponse,
  type PresetId,
  type PublicConfig,
} from "@mantra/shared";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { Field, Segmented, TopBar } from "../components/ui";
import { api, getConfig, isOnline } from "../lib/api";
import { audioContext, playOnce } from "../lib/audio/engine";
import { unlockAudio } from "../lib/audio/unlock";
import { db, kvGet } from "../lib/db";
import { createSet, getProfile, liveAffirmations, replaceAffirmations, updateSet } from "../lib/repo";
import { navigate } from "../lib/router";
import { ensureClips, loadBuffer } from "../lib/tts";
import { useVoices, LOADING_NOTE, pickVoice, voiceOptions } from "../lib/useVoices";
import { isUserVoice } from "@mantra/shared";

const EXAMPLES_SHOWN = 5;

function pickExamples(): string[] {
  const a = [...INTENT_EXAMPLES];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a.slice(0, EXAMPLES_SHOWN);
}

interface Line {
  key: number;
  text: string;
  /** предложено приложением и не тронуто человеком — такие строки «Обновить» заменяет */
  suggested: boolean;
}

let keySeq = 0;
const toLines = (texts: string[], suggested: boolean): Line[] => texts.map((text) => ({ key: ++keySeq, text, suggested }));

/** Ввод намерения → редактируемый список → голос → сохранить. Используется и для правки набора. */
export function Composer({ setId }: { setId?: string }) {
  const [source, setSource] = useState("");
  const [lines, setLines] = useState<Line[] | null>(null);
  const [title, setTitle] = useState("");
  const [preset, setPreset] = useState<PresetId>("calm");
  const [voiceId, setVoiceId] = useState<string>(DEFAULT_VOICE);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const config = useLiveQuery(() => kvGet<PublicConfig>("config"));
  const voices = useVoices();
  // выбранный голос пропал из списка (сервер перезапустился, голос удалён) — берём первый доступный
  useEffect(() => {
    // пропавший голос → первый готовый; загружающийся голос, выбранный раньше, оставляем —
    // фоновая озвучка дождётся, когда он прогреется
    if (voices.length && !voices.some((v) => v.id === voiceId)) setVoiceId((voices.find((v) => !v.loading) ?? voices[0]!).id);
  }, [voices, voiceId]);
  useEffect(() => {
    void getConfig();
  }, []);
  const listRef = useRef<HTMLOListElement>(null);
  const [examples, setExamples] = useState(pickExamples);
  // всё, что уже предлагали в этой сессии, — чтобы «Обновить» не повторялся
  const shown = useRef<Set<string>>(new Set());
  const round = useRef(0);

  useEffect(() => {
    void (async () => {
      const p = await getProfile();
      if (!setId) {
        setPreset(p.defaultPreset);
        setVoiceId(await pickVoice(p.defaultVoice, p.gender));
        return;
      }
      const s = await db.sets.get(setId);
      if (!s) return;
      setTitle(s.title);
      setPreset(s.preset);
      setVoiceId(s.voiceId);
      setSource(s.sourceText ?? "");
      setLines(toLines((await liveAffirmations(setId)).map((a) => a.text), false));
    })();
  }, [setId]);

  /**
   * С LLM на сервере — составляем через неё; без сети или без LLM — словарём на устройстве.
   * Если LLM не ответила, показываем фразы из словаря, чтобы человек не остался ни с чем.
   */
  const suggest = async (count: number, seed: number): Promise<string[]> => {
    const { gender } = await getProfile();
    const exclude = [...shown.current];
    if (config?.llm && isOnline()) {
      try {
        const r = await api<ComposeResponse>("/v1/compose", {
          method: "POST",
          json: { text: source, gender, max: count, seed, exclude: exclude.slice(-60) },
        });
        if (r.affirmations.length) return r.affirmations;
      } catch {
        setError("Сервер не ответил — подобрали фразы на устройстве.");
      }
    }
    return composeAffirmations(source, { gender, max: count, seed, exclude }).map((a) => a.text);
  };

  const remember = (texts: string[]) => texts.forEach((t) => shown.current.add(t));

  const compose = async () => {
    setError(null);
    if (!title) setTitle(suggestTitle(source));
    shown.current = new Set();
    round.current = 0;
    setBusy("Составляю аффирмации…");
    try {
      const out = await suggest(8, 0);
      remember(out);
      setLines(toLines(out, true));
    } finally {
      setBusy(null);
    }
  };

  /** «Обновить аффирмации»: заменяем только нетронутые предложения, свои и исправленные строки остаются */
  const refresh = async () => {
    if (!lines) return;
    setError(null);
    const keep = lines.filter((l) => !l.suggested && l.text.trim());
    lines.forEach((l) => shown.current.add(l.text.trim()));
    const need = Math.max(1, 8 - keep.length);
    round.current += 1;
    setBusy("Подбираю другие…");
    try {
      const keepSet = new Set(keep.map((l) => l.text.trim().toLowerCase()));
      const fresh = (await suggest(need + keep.length, round.current)).filter((t) => !keepSet.has(t.toLowerCase()));
      const next = fresh.slice(0, need);
      if (!next.length) {
        setError("Похожих фраз больше нет — уточните запрос или допишите свои.");
        return;
      }
      remember(next);
      setLines([...keep, ...toLines(next, true)]);
    } finally {
      setBusy(null);
    }
  };

  const texts = (lines ?? []).map((l) => l.text.trim()).filter(Boolean);
  const textsKey = texts.join("\n");

  // Озвучиваем заранее, пока человек читает и правит фразы: к нажатию «Слушать» клипы уже готовы.
  // Шаблонные фразы общие для всех — чаще всего это попадание в кэш, а не синтез.
  useEffect(() => {
    if (!textsKey || !isOnline() || isUserVoice(voiceId)) return;
    const t = setTimeout(() => {
      void ensureClips(textsKey.split("\n").map((text) => ({ text, voiceId, preset }))).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [textsKey, preset, voiceId]);

  const preview = async () => {
    unlockAudio(); // до загрузки клипа — пока действует жест пользователя
    const text = texts[0] ?? "Я в безопасности здесь и сейчас.";
    setPreviewing(true);
    setError(null);
    try {
      const slow = setTimeout(
        () => setError(isUserVoice(voiceId) ? "Озвучиваю вашим голосом — первая фраза займёт около минуты." : "Этот голос озвучивает не мгновенно — ещё несколько секунд."),
        isUserVoice(voiceId) ? 0 : 4000,
      );
      const [clip] = await ensureClips([{ text, voiceId, preset }]).finally(() => clearTimeout(slow));
      setError(null);
      await playOnce(await loadBuffer(audioContext(), clip!));
    } catch {
      setError("Не удалось загрузить голос. Проверьте подключение к интернету.");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!texts.length) return;
    setBusy("Сохраняю…");
    let id = setId;
    if (id) {
      await updateSet(id, { title: title.trim() || suggestTitle(source), preset, voiceId, sourceText: source || null });
      await replaceAffirmations(id, texts);
    } else {
      id = await createSet({
        title: title.trim() || suggestTitle(source),
        sourceText: source || null,
        preset,
        voiceId,
        settings: DEFAULT_SESSION_SETTINGS,
        texts,
      });
    }
    setBusy(null);
    navigate({ name: "play", id }, { replace: true });
  };

  const update = (key: number, text: string) =>
    setLines((ls) => ls!.map((l) => (l.key === key ? { ...l, text, suggested: false } : l)));
  const remove = (key: number) => setLines((ls) => ls!.filter((l) => l.key !== key));
  const move = (i: number, d: -1 | 1) =>
    setLines((ls) => {
      const next = [...ls!];
      const [x] = next.splice(i, 1);
      next.splice(i + d, 0, x!);
      return next;
    });
  const add = () => {
    setLines((ls) => [...(ls ?? []), { key: ++keySeq, text: "", suggested: false }]);
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLTextAreaElement>("li:last-child textarea")?.focus());
  };

  return (
    <div className="composer">
      {!setId && (
        <Field label="Чего вы хотите?">
          <textarea
            className="intent"
            rows={4}
            maxLength={4000}
            value={source}
            placeholder="Например: хочу меньше тревожиться, спокойно засыпать и чувствовать себя увереннее на работе"
            onChange={(e) => setSource(e.target.value)}
          />
          {!source && (
            <div className="examples">
              {examples.map((e) => (
                <button key={e} className="chip" onClick={() => setSource(e)}>
                  {e}
                </button>
              ))}
              <button className="chip chip-more" onClick={() => setExamples(pickExamples())}>
                Другие примеры
              </button>
            </div>
          )}
          <button
            className={lines ? "btn ghost" : "btn primary wide"}
            disabled={source.trim().length < 3 || !!busy}
            onClick={() => void compose()}
          >
            {busy === "Составляю аффирмации…" ? busy : lines ? "Составить заново" : "Составить аффирмации"}
          </button>
        </Field>
      )}

      {lines && (
        <>
          <Field label="Аффирмации" hint="Звучат по порядку. Поправьте слова, чтобы они звучали как ваши.">
            <ol className="lines" ref={listRef}>
              {lines.map((l, i) => (
                <li key={l.key} className="line">
                  <textarea
                    rows={1}
                    value={l.text}
                    maxLength={MAX_AFFIRMATION_LEN}
                    aria-label={`Фраза ${i + 1}`}
                    onChange={(e) => update(l.key, e.target.value)}
                  />
                  <div className="line-tools">
                    <button className="icon-btn sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Выше">
                      <Icon name="up" size={18} />
                    </button>
                    <button className="icon-btn sm" disabled={i === lines.length - 1} onClick={() => move(i, 1)} aria-label="Ниже">
                      <Icon name="down" size={18} />
                    </button>
                    <button className="icon-btn sm" onClick={() => remove(l.key)} aria-label="Удалить фразу">
                      <Icon name="close" size={18} />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
            <div className="row-actions">
              <button className="btn ghost" onClick={add}>
                <Icon name="plus" size={18} /> Добавить фразу
              </button>
              {source.trim().length >= 3 && (
                <button className="btn ghost" disabled={!!busy} onClick={() => void refresh()}>
                  <Icon name="refresh" size={18} /> {busy === "Подбираю другие…" ? busy : "Обновить аффирмации"}
                </button>
              )}
            </div>
          </Field>

          <Field label="Название">
            <input className="input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
          </Field>

          <Field label="Голос">
            {voices.length > 1 && (
              <Segmented label="Голос" value={voiceId} onChange={setVoiceId} options={voiceOptions(voices)} />
            )}
            {voices.some((v) => v.loading) && <p className="muted small">{LOADING_NOTE}</p>}
            <Segmented
              label="Манера голоса"
              value={preset}
              onChange={setPreset}
              options={PRESETS.map((p) => ({ value: p, label: PRESET_LABELS[p] }))}
            />
            <button className="btn ghost" disabled={previewing} onClick={() => void preview()}>
              <Icon name="ear" size={18} /> {previewing ? "Загружаю…" : "Послушать"}
            </button>
          </Field>

          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}

          <button className="btn primary wide" disabled={!texts.length || !!busy} onClick={() => void save()}>
            {busy ?? (setId ? "Сохранить" : "Сохранить и слушать")}
          </button>
        </>
      )}
      {!lines && error && <p className="error">{error}</p>}
    </div>
  );
}

export { pickVoice };

export function ComposeScreen({ setId }: { setId?: string }) {
  return (
    <main className="screen">
      <TopBar title={setId ? "Правка настроя" : "Новый настрой"} />
      <Composer setId={setId} />
    </main>
  );
}
